import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

declare global {
  interface Window {
    CardSDK: any;
  }
}

interface UseTapSDKProps {
  onSuccess?: (tokenId: string) => void;
  onError?: (error: any) => void;
}

// مهلة انتظار تحميل مكتبة Tap SDK (30 ثانية)
const SDK_LOAD_TIMEOUT_MS = 30000;

export const useTapSDK = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    // التحقق من وجود المفاتيح في متغيرات البيئة
    const rawPublicKey = import.meta.env.VITE_TAP_PUBLISHABLE_KEY || '';
    const rawMerchantId = import.meta.env.VITE_TAP_MERCHANT_ID || '';
    
    const publicKey = rawPublicKey.trim();
    const merchantId = rawMerchantId.trim();

    if (!publicKey) {
      const msg = '❌ VITE_TAP_PUBLISHABLE_KEY غير موجود في متغيرات البيئة (.env)';
      setError(msg);
      Sentry.captureMessage('Tap SDK: Missing VITE_TAP_PUBLISHABLE_KEY', { level: 'error', tags: { source: 'tap_sdk_init' } });
      return;
    }

    if (!merchantId) {
      const msg = '❌ VITE_TAP_MERCHANT_ID غير موجود في متغيرات البيئة (.env)';
      setError(msg);
      Sentry.captureMessage('Tap SDK: Missing VITE_TAP_MERCHANT_ID', { level: 'error', tags: { source: 'tap_sdk_init' } });
      return;
    }

    // التحقق من صيغة المفتاح
    const isTestKey = publicKey.startsWith('pk_test_');
    const isLiveKey = publicKey.startsWith('pk_live_');
    
    if (!isTestKey && !isLiveKey) {
      const msg = '❌ المفتاح العام غير صحيح (يجب أن يبدأ بـ pk_test_ أو pk_live_)';
      setError(msg);
      Sentry.captureMessage('Tap SDK: Invalid public key format', { level: 'error', tags: { source: 'tap_sdk_init' } });
      return;
    }

    if (isLiveKey && publicKey.includes('pk_live_0x') && !publicKey.includes('pk_live_Ox')) {
        const msg = '❌ خطأ في المفتاح: يبدأ بـ pk_live_0x بدلاً من pk_live_Ox';
        setError(msg);
        Sentry.captureMessage('Tap SDK: Malformed live key (0x vs Ox)', { level: 'error', tags: { source: 'tap_sdk_init' } });
        return;
    }

    // التحقق من تحميل المكتبة مع مهلة زمنية
    let timeoutId: any;
    let attempts = 0;
    const maxAttempts = SDK_LOAD_TIMEOUT_MS / 500;

    const checkSDK = () => {
      if (window.CardSDK) {
        setIsLoaded(true);
        return;
      }
      attempts++;
      if (attempts >= maxAttempts) {
        const msg = 'Tap Card SDK script failed to load within 30 seconds';
        setError('❌ فشل تحميل مكتبة بوابة الدفع. يرجى تحديث الصفحة.');
        Sentry.captureMessage(msg, {
          level: 'error',
          tags: { source: 'tap_sdk_load', gateway: 'tap' },
          extra: { attempts, timeout_ms: SDK_LOAD_TIMEOUT_MS }
        });
        return;
      }
      timeoutId = setTimeout(checkSDK, 500);
    };

    checkSDK();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return { 
    isLoaded, 
    error, 
    publicKey: import.meta.env.VITE_TAP_PUBLISHABLE_KEY?.trim(),
    merchantId: import.meta.env.VITE_TAP_MERCHANT_ID?.trim()
  };
};

